import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { ActivateVenueAgentResultDto } from "../../../models/court-recording/dto/venue-agent.dto";
import { GET_ORGANIZATION_FEATURE_FLAGS_QUERY_KEY } from "../../organization/hooks/useGetOrganizationFeatureFlags";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { GET_VENUE_AGENTS_QUERY_KEY } from "./useListVenueAgents";

/**
 * The go-live step for a pre-provisioned agent: from here its courts are
 * player-visible. Minting a key no longer does this (venue-agent
 * pre-provisioning), so a box can ship with its credentials loaded and have
 * cameras assigned remotely before anyone can book a recording on it.
 *
 * Idempotent on the API side, so a double-click is harmless. On success the
 * org's recording gates are refreshed too — the feature-flags query and the
 * picker's cached `recordingAvailable` — or the admin booking modal would keep
 * hiding the recording toggle until its 5-minute staleTime ran out.
 */
export const useActivateVenueAgent = (organizationId: string) => {
    const client = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (agentId: string) =>
            client.fetchApi<ActivateVenueAgentResultDto, {}>({
                endpoint: `court-recordings/admin/venue-agents/${agentId}/activate?organizationId=${organizationId}`,
                method: 'POST',
            }),
        onSuccess: (result) => {
            queryClient.invalidateQueries({
                queryKey: GET_VENUE_AGENTS_QUERY_KEY(organizationId),
            });
            queryClient.invalidateQueries({
                queryKey: GET_ORGANIZATION_FEATURE_FLAGS_QUERY_KEY(organizationId),
            });
            // Read at settle time, not render time: the picker may have moved.
            // Only the org this was done for — never write one tenant's flag
            // onto another tenant's cached organization.
            const { selectedOrganization, setSelectedOrganization } =
                useSelectedOrganization.getState();
            if (selectedOrganization?.id === organizationId) {
                setSelectedOrganization({
                    ...selectedOrganization,
                    recordingAvailable: result.recordingAvailable,
                });
            }
        },
    });
};
